import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Plus, FileText } from "lucide-react";

function tierBadge(label) {
  const c = {
    Invisible:          "#c46a5a",
    "Under-represented": "#d4a04a",
    "Underrepresented": "#d4a04a",
    Emerging:           "#b8963e",
    Recommended:        "#6db66d",
  }[label] || "#5e5448";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono-eav tracking-wider" style={{ color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {(label || "—").toUpperCase()}
    </span>
  );
}

const statusLabels = {
  queued:             { c: "#8a7e6d", t: "QUEUED" },
  running:            { c: "#d4a04a", t: "RUNNING" },
  processing:         { c: "#d4a04a", t: "RUNNING" },
  awaiting_input:     { c: "#d4a04a", t: "NEEDS INPUT" },
  ready_for_scoring:  { c: "#d4a04a", t: "READY TO SCORE" },
  complete:           { c: "#6db66d", t: "COMPLETE" },
  failed:             { c: "#c46a5a", t: "FAILED" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [audits, setAudits] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/audits");
        if (!cancel) setAudits(data);
      } catch {}
    };
    load();
    const interval = setInterval(load, 4000);
    return () => { cancel = true; clearInterval(interval); };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Dashboard</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
            Welcome, <em className="text-[#b8963e] italic font-normal">{user?.name?.split(" ")[0] || "there"}.</em>
          </h1>
          <p className="mt-3 text-[#c9bca4]">Your snapshots, history, and downloadable reports.</p>
        </div>
        <Link to="/audit/new" data-testid="dashboard-new-audit-button" className="btn-gold">
          <Plus className="w-4 h-4" /> New Snapshot
        </Link>
      </div>

      <div className="mt-12 eav-card overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-[#2a2620] bg-[#0a0908] font-mono-eav text-[10px] tracking-[0.2em] text-[#8a7e6d] uppercase">
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Industry</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1">Score</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-1">Pipeline</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {!audits && (
          <div className="p-6 text-[#8a7e6d] text-sm font-mono-eav" data-testid="dashboard-loading">Loading…</div>
        )}
        {audits && audits.length === 0 && (
          <div className="p-14 text-center" data-testid="dashboard-empty">
            <FileText className="w-8 h-8 text-[#5e5448] mx-auto" />
            <p className="mt-5 text-[#f4efe6] font-serif-display font-bold text-xl">No snapshots yet</p>
            <p className="mt-2 text-sm text-[#8a7e6d]">Run your first AI Visibility Snapshot now.</p>
            <Link to="/audit/new" data-testid="dashboard-empty-cta" className="mt-6 inline-flex btn-gold">
              <Plus className="w-4 h-4" /> Create snapshot
            </Link>
          </div>
        )}
        {audits && audits.map((a) => {
          const sl = statusLabels[a.status] || { c: "#8a7e6d", t: a.status?.toUpperCase() };
          let target;
          if (a.status === "complete") target = `/snapshot/${a.id}`;
          else if (a.status === "failed") target = null;
          else if (a.mode === "manual" && a.status === "awaiting_input") target = `/audit/${a.id}/manual`;
          else if (a.mode === "manual" && a.status === "ready_for_scoring") target = `/audit/${a.id}/score`;
          else target = `/processing/${a.id}`;
          const RowInner = (
            <div className="grid grid-cols-12 px-6 py-4 border-b border-[#1a1815] hover:bg-[#121110] cursor-pointer transition-colors items-center text-sm">
              <div className="col-span-3">
                <div className="font-medium text-[#f4efe6]">{a.company_name}</div>
                <div className="text-xs text-[#5e5448] font-mono-eav mt-0.5">{a.website}</div>
              </div>
              <div className="col-span-2 text-[#c9bca4]">{a.industry}</div>
              <div className="col-span-2 text-[#8a7e6d] font-mono-eav text-xs">
                {a.created_at ? new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              </div>
              <div className="col-span-1">
                {a.total_score != null
                  ? <span className="font-serif-display font-bold text-2xl text-[#f4efe6]">{a.total_score}</span>
                  : <span className="text-[#5e5448]">—</span>}
              </div>
              <div className="col-span-2">{tierBadge(a.tier_label)}</div>
              <div className="col-span-1">
                <span className="font-mono-eav text-[10px] tracking-wider uppercase text-[#b8963e]">
                  {(a.pipeline_status || "new").replace("_", " ")}
                </span>
              </div>
              <div className="col-span-1 text-right font-mono-eav text-xs" style={{ color: sl.c }}>● {sl.t}</div>
            </div>
          );
          return target ? (
            <Link key={a.id} to={target} data-testid={`dashboard-row-${a.id}`} className="block">{RowInner}</Link>
          ) : (
            <div key={a.id} data-testid={`dashboard-row-${a.id}`}>{RowInner}</div>
          );
        })}
      </div>
    </div>
  );
}
